import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, IconButton, Link as MuiLink, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useAuth } from "@healthdesk/shared-hooks";
import logoIcon from "../../assets/images/logos/logo-icon.png";
import AccountSetupFlow, { ACCOUNT_SETUP_STEPS } from "./AccountSetupFlow";

/**
 * Onboarding page - For users who have Firebase Auth but need to complete profile setup
 *
 * Uses the shared AccountSetupFlow component for the actual form steps.
 * Supports ?role=patient|provider|facility param to pre-select account type.
 */
const Onboarding = () => {
  const { user, userData, userLoading, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedRole = searchParams.get("role");
  const [loading, setLoading] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [step, setStep] = useState(ACCOUNT_SETUP_STEPS.ACCOUNT_TYPE);
  const [isOnSuccessStep, setIsOnSuccessStep] = useState(false);
  const signupInProgressRef = useRef(false);
  const navigate = useNavigate();
  const backHandlerRef = useRef(null);

  // Check auth state and redirect if needed
  useEffect(() => {
    if (userLoading) return;

    if (user) {
      // If userData is already complete, redirect to dashboard
      // BUT only if we're not in the middle of signup (prevents race condition)
      if (userData?.role && !userData.onboarding && !signupInProgressRef.current) {
        navigate("/dashboard");
        return;
      }
      setLoading(false);
    } else {
      // No authenticated user - redirect to phone auth
      navigate("/auth");
    }
  }, [navigate, user, userData, userLoading]);

  const handleComplete = () => {
    navigate("/dashboard");
  };

  const handleBack = () => {
    backHandlerRef.current?.();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "background.paper",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 4,
      }}
    >
      {/* Logo */}
      <Box
        component="img"
        src={logoIcon}
        alt="HealthDesk"
        sx={{ height: 48, mb: 4 }}
      />

      {/* Main content container - matches PhoneAuthFlow width */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          mx: "auto",
          px: 3,
        }}
      >
        {/* Back button - show after first step, hide on success */}
        {step !== ACCOUNT_SETUP_STEPS.ACCOUNT_TYPE && !isOnSuccessStep && (
          <Box sx={{ mb: 2 }}>
            <IconButton onClick={handleBack} edge="start" sx={{ ml: -1 }}>
              <ArrowBack />
            </IconButton>
          </Box>
        )}

        {/* Account setup form */}
        <AccountSetupFlow
          onComplete={handleComplete}
          onStepChange={setStep}
          backHandlerRef={backHandlerRef}
          preselectedRole={preselectedRole}
          onSignupStart={() => {
            signupInProgressRef.current = true;
          }}
          onSuccessStep={setIsOnSuccessStep}
        />

        {/* Footer with logout - hide on success step */}
        {!isOnSuccessStep && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 4, textAlign: "center", display: "block" }}
          >
            Wrong account?{" "}
            <MuiLink
              component="button"
              variant="caption"
              onClick={async () => {
                setLogoutLoading(true);
                try {
                  await logout();
                  navigate("/");
                } catch (err) {
                  console.error("Logout error:", err);
                  setLogoutLoading(false);
                }
              }}
              disabled={logoutLoading}
              sx={{ verticalAlign: "baseline" }}
            >
              {logoutLoading ? "Logging out..." : "Log out"}
            </MuiLink>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default Onboarding;
